import {
  BreadcrumbHeading,
  CancelledIcon,
  DetailsPanel,
  EditIcon,
  FormatInformation,
  IconButton,
  Spinner,
  Typography,
  useSnackbar,
  SnackbarStatus,
} from '@weareopenr/component-library';
import styles from './User.module.scss';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IBrandVenueAccess, IRole, IUser } from 'src/types';
import { getUser as getUserApi } from 'src/api/getUser';
import { QueryKeys } from 'src/constants/QueryKeys';
import { useRoles } from 'src/hooks/useRoles/useRoles';
import { useState } from 'react';
import UserModal from 'src/components/UserModal/UserModal';
import { usePermissions, useUserTracking } from 'src/hooks';
import { useGetBrands } from 'src/hooks/useGetBrands/useGetBrands';
import { IBrandResponse } from 'src/api/getBrands/types';
import { InformationField } from 'src/components/InformationField';
import { useDeactivateUser } from 'src/components/DeactivateUser';
import { IOption } from '@weareopenr/component-library/build/components/Dropdown/Dropdown';
import { usePermissionCatalogue } from 'src/hooks/usePermissionCatalogue/usePermissionCatalogue';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { getUserVenueAccess } from 'src/api/getUserVenueAccess';
import RoleSummary from 'src/components/RoleModal/RoleSummary';
import { BrandVenueDetails } from './BrandVenueDetails';
import { IApplicationSection } from 'src/components/RoleModal/permissions';

const resolveUserBrands = (user: IUser | undefined, brands: IBrandResponse[]): IBrandResponse[] => {
  if (user?.brands?.includes('*')) return brands;
  return (
    user?.brands
      ?.map((brandId) => brands?.find((brand) => brand.brand_id === brandId))
      .filter((brand): brand is IBrandResponse => brand !== undefined) || []
  );
};

const PermissionsInformation = ({
  role,
  sections,
  isLoading,
}: {
  role?: IRole;
  sections: IApplicationSection[];
  isLoading: boolean;
}) => {
  if (!role?.permissions?.length) return <>Base Permissions Only</>;
  // Without the catalogue every permission would render as an unrecognised
  // raw slug, so hold the summary back until the sections are available.
  if (isLoading) return <Spinner size="small" />;
  return <RoleSummary sections={sections} selectedPermissions={role.permissions} />;
};

const BrandsInformation = ({
  userBrands,
  venueAccess,
  isWildcard,
  venueAccessEnabled,
  onError,
}: {
  userBrands: IBrandResponse[];
  venueAccess: IBrandVenueAccess[];
  isWildcard: boolean;
  venueAccessEnabled: boolean;
  onError: (error: Error) => void;
}) => {
  if (userBrands.length === 0) {
    return <Typography size="medium">No brands assigned</Typography>;
  }

  return (
    <div className={styles['brand-cards']}>
      {userBrands.map((brand) => (
        <BrandVenueDetails
          key={brand.brand_id}
          brand={brand}
          entry={venueAccess.find((entry) => entry.brand_id === brand.brand_id)}
          isWildcard={isWildcard}
          venueAccessEnabled={venueAccessEnabled}
          onError={onError}
        />
      ))}
    </div>
  );
};

export const User = () => {
  const { userId } = useParams<{ userId: string }>();
  const { showSnackbar } = useSnackbar();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const { canEditUsers, canDeactivateUsers } = usePermissions();
  const { data: brands = [] } = useGetBrands();
  const { captureException } = useUserTracking();
  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery<IUser>({
    queryKey: [QueryKeys.USERS, userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      const response = await getUserApi(userId);
      return response.data;
    },
    enabled: !!userId,
    onError: (error) => {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst fetching brands',
      });
      captureException(error);
    },
  });
  const { roles, isLoading: isRolesLoading } = useRoles();
  const role = roles?.find((role) => role.slug === user?.role);
  const { sections, isLoading: isCatalogueLoading } = usePermissionCatalogue();
  const { venueAccessManagement } = useFeatureFlags();

  const isWildcard = !!user?.brands?.includes('*');
  const handleAccessError = (error: Error) => {
    showSnackbar({
      status: SnackbarStatus.ERROR,
      message: 'Something went wrong whilst fetching venue access',
    });
    captureException(error);
  };
  const { data: venueAccessData } = useQuery({
    queryKey: [QueryKeys.USER_VENUE_ACCESS, userId],
    queryFn: () => getUserVenueAccess(userId as string),
    enabled: !!userId && !!user && !isWildcard && venueAccessManagement,
    onError: handleAccessError,
  });
  const venueAccess = venueAccessData?.data ?? [];

  const deactivateUserHook = useDeactivateUser();

  if (isLoading || isRolesLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="large" />
      </div>
    );
  }

  const handleEditClick = canEditUsers ? () => setShowEditModal(true) : undefined;

  const headerActions: IOption[] = [];
  if (canDeactivateUsers && user) {
    headerActions.push({
      id: 'deactivate',
      name: 'Deactivate',
      onOptionClick: () => deactivateUserHook.openPrompt(user),
      icon: CancelledIcon,
      hoverColour: 'tomato',
    });
  }

  const userBrands = resolveUserBrands(user, brands);

  return (
    <>
      {canEditUsers && (
        <UserModal
          show={showEditModal}
          closeModal={() => {
            refetch();
            setShowEditModal(false);
          }}
          user={user}
        />
      )}
      {deactivateUserHook.PromptComponent}
      <div className={styles.container}>
        <BreadcrumbHeading
          breadcrumbs={[
            { label: 'Users', path: '/users' },
            { label: `${user?.first_name} ${user?.last_name}`, path: `/users/${user?.id}` },
          ]}
          optionMenuItems={headerActions.length > 0 ? headerActions : undefined}
        />
        <div className={styles['details-container']}>
          <DetailsPanel title="User Details">
            <FormatInformation heading="First Name">{user?.first_name}</FormatInformation>
            <FormatInformation heading="Last Name">{user?.last_name}</FormatInformation>
            <FormatInformation heading="Email">{user?.email}</FormatInformation>
          </DetailsPanel>
          <DetailsPanel
            title="Access"
            action={
              canEditUsers ? (
                <IconButton
                  onClick={handleEditClick}
                  icon={EditIcon}
                  aria-label="Edit User Access"
                />
              ) : undefined
            }
          >
            <div className={styles['access-columns']}>
              <div className={styles['access-column']}>
                <FormatInformation heading="Role">{role?.name}</FormatInformation>
                <InformationField
                  heading="Permissions"
                  information={
                    <PermissionsInformation
                      role={role}
                      sections={sections}
                      isLoading={isCatalogueLoading}
                    />
                  }
                />
              </div>
              <div className={styles['access-column']}>
                <InformationField
                  heading="Brands"
                  information={
                    <BrandsInformation
                      userBrands={userBrands}
                      venueAccess={venueAccess}
                      isWildcard={isWildcard}
                      venueAccessEnabled={venueAccessManagement}
                      onError={handleAccessError}
                    />
                  }
                />
              </div>
            </div>
          </DetailsPanel>
        </div>
      </div>
    </>
  );
};
